import React, { useState } from "react";
import { toast } from "sonner";
import { errMsg, fmt, useApi, useLoad, useTaxonomy } from "../../lib/api";
import { Empty, ErrorBox, Field, Loading, SponsoredTag, StatusBadge } from "../../components/kit";

export function VendorAds({ isOwner }: { isOwner: boolean }) {
  const api = useApi();
  const { categories } = useTaxonomy();
  const { data, loading, error, reload } = useLoad<any[]>("/vendor/ads");
  const [v, setV] = useState({ title: "", body: "", category_id: "" });
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await api("post", "/vendor/ads", { ...v, category_id: v.category_id || null }); toast.success("Ad submitted for ops review"); setV({ title: "", body: "", category_id: "" }); reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div>
        <p className="mb-3 text-sm text-[var(--muted)]"><SponsoredTag /> Ads appear only in labelled sponsored boxes and never change organic matching.</p>
        {loading && !data && <Loading />}
        {error && <ErrorBox message={error} onRetry={reload} />}
        {data?.length === 0 && <Empty title="No ads submitted" />}
        <ul className="space-y-2">
          {data?.map((a) => (
            <li key={a.id} className="card text-sm" data-testid={`vendor-ad-${a.id}`}>
              <div className="flex justify-between gap-2"><strong>{a.title}</strong><StatusBadge value={a.status} /></div>
              <p className="text-[var(--muted)]">{a.body}</p>
              <p className="mono mt-1 text-xs">impressions {a.impressions || 0} · clicks {a.clicks || 0}</p>
            </li>
          ))}
        </ul>
      </div>
      {isOwner ? (
        <form onSubmit={submit} className="card space-y-4" data-testid="vendor-ad-form">
          <h3 className="text-2xl font-bold">Submit an ad (test only)</h3>
          <Field label="Title" id="ad-title"><input id="ad-title" required minLength={3} maxLength={80} className="input" value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} data-testid="vendor-ad-title-input" /></Field>
          <Field label="Body" id="ad-body"><textarea id="ad-body" required minLength={3} maxLength={240} rows={3} className="input" value={v.body} onChange={(e) => setV({ ...v, body: e.target.value })} data-testid="vendor-ad-body-input" /></Field>
          <Field label="Target trade (optional)" id="ad-cat">
            <select id="ad-cat" className="input" value={v.category_id} onChange={(e) => setV({ ...v, category_id: e.target.value })} data-testid="vendor-ad-category-select">
              <option value="">All trades</option>{categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <button className="btn btn-primary" data-testid="vendor-ad-submit-button">Submit for review</button>
        </form>
      ) : <Empty title="Only owners submit ads" />}
    </div>
  );
}

export function VendorTeam({ org, isOwner }: { org: any; isOwner: boolean }) {
  const api = useApi();
  const [email, setEmail] = useState("");
  if (org.loading && !org.data) return <Loading />;
  if (org.error) return <ErrorBox message={org.error} onRetry={org.reload} />;
  const d = org.data;
  if (!d) return null;
  const invite = async (e: React.FormEvent) => {
    e.preventDefault();
    try { await api("post", "/vendor/invitations", { email }); toast.success("Staff invitation recorded (no email sent)"); setEmail(""); org.reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  const patch = async (mid: string, body: any) => {
    try { await api("patch", `/vendor/members/${mid}`, body); toast.success("Member updated"); org.reload(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="card min-w-0 space-y-3">
        <h3 className="text-2xl font-bold">Team</h3>
        <ul className="space-y-1 text-sm" data-testid="vendor-team-list">
          {d.team.map((m: any) => (
            <li key={m.id} className="flex flex-wrap items-center justify-between gap-2" data-testid={`vendor-member-${m.id}`}>
              <span className="min-w-0 break-all">{m.name} <span className="text-[var(--muted)]">{m.email}</span></span>
              <span className="flex flex-wrap items-center gap-1"><StatusBadge value={m.role} />{m.status !== "active" && <StatusBadge value={m.status} />}
                {isOwner && <button className="btn btn-sm btn-ghost" onClick={() => patch(m.id, { role: m.role === "owner" ? "staff" : "owner" })} data-testid={`vendor-member-role-${m.id}`}>{m.role === "owner" ? "Make staff" : "Make owner"}</button>}
                {isOwner && <button className="btn btn-sm btn-danger" onClick={() => patch(m.id, { status: m.status === "active" ? "inactive" : "active" })} data-testid={`vendor-member-status-${m.id}`}>{m.status === "active" ? "Deactivate" : "Reactivate"}</button>}
              </span>
            </li>
          ))}
          {d.pending_invites.map((i: any) => <li key={i.id} className="flex justify-between gap-2 text-[var(--muted)]"><span className="min-w-0 break-all">{i.email}</span><StatusBadge value="pending" /></li>)}
        </ul>
        {isOwner && (
          <form onSubmit={invite} className="flex flex-wrap items-end gap-2 pt-2">
            <div className="min-w-[200px] flex-1"><Field label="Invite staff by email" id="staff-email"><input id="staff-email" type="email" required className="input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="vendor-staff-email-input" /></Field></div>
            <button className="btn btn-dark" data-testid="vendor-staff-invite-button">Record invite</button>
          </form>
        )}
      </div>
      <div className="card min-w-0 space-y-2" data-testid="vendor-subscription-card">
        <h3 className="text-2xl font-bold">Plan</h3>
        <span className="badge badge-amber">{d.subscription?.source === "stripe" ? "Paid via Stripe (test mode)" : "Test subscription · no payment"}</span>
        <p className="text-sm">Tier: <strong data-testid="vendor-subscription-tier">{d.subscription?.tier}</strong> · <StatusBadge value={d.subscription?.status} /></p>
        <p className="text-xs text-[var(--muted)]">Managed by Verge operations or paid in the Billing tab. Updated {fmt(d.subscription?.updated_at)}. Your plan never changes organic matching.</p>
      </div>
    </div>
  );
}
